import { useState, useEffect } from 'react';
import { CheckCircle, Car, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Montreal area cities for realistic locations
const MONTREAL_AREAS = [
  'Montréal', 'Laval', 'Longueuil', 'Brossard', 'Terrebonne', 
  'Repentigny', 'Saint-Jean-sur-Richelieu', 'Châteauguay', 
  'Saint-Jérôme', 'Drummondville', 'Granby', 'Blainville',
  'Mirabel', 'Mascouche', 'Vaudreuil-Dorion', 'Saint-Hyacinthe'
];

// Get random location
const getLocation = (index) => {
  return MONTREAL_AREAS[index % MONTREAL_AREAS.length];
};

export function RecentlySoldTicker() {
  const { t } = useTranslation();
  const [soldVehicles, setSoldVehicles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const getTimeAgo = (index) => {
    const times = [
      t('recentlySold.hoursAgo', { count: 2 }),
      t('recentlySold.hoursAgo', { count: 5 }),
      t('recentlySold.yesterday'),
      t('recentlySold.daysAgo', { count: 2 }),
      t('recentlySold.daysAgo', { count: 3 }),
      t('recentlySold.thisWeek'),
      t('recentlySold.lastWeek')
    ];
    return times[index % times.length];
  };

  useEffect(() => {
    // Fetch sold vehicles
    const fetchSold = async () => {
      try {
        const response = await axios.get(`${API}/vehicles?limit=100&include_offline=true`);
        const sold = response.data
          .filter(v => v.status === 'sold')
          .slice(0, 10);
        
        if (sold.length > 0) {
          setSoldVehicles(sold);
          setIsVisible(true);
        }
      } catch (error) {
        console.error('Error fetching sold vehicles:', error);
      }
    };

    fetchSold();
  }, []);

  useEffect(() => {
    if (soldVehicles.length === 0) return;

    // Rotate through sold vehicles
    const interval = setInterval(() => {
      setIsVisible(false);
      
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % soldVehicles.length);
        setIsVisible(true);
      }, 500);
    }, 5000); // Show each for 5 seconds

    return () => clearInterval(interval);
  }, [soldVehicles.length]);

  if (soldVehicles.length === 0) return null;

  const vehicle = soldVehicles[currentIndex];
  const timeAgo = getTimeAgo(currentIndex);
  const location = getLocation(currentIndex);

  return (
    <div className="fixed bottom-4 left-4 z-50 max-w-sm">
      <div 
        className={`bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden transition-all duration-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-4 py-2">
          <div className="flex items-center gap-2 text-white text-sm font-medium">
            <CheckCircle className="h-4 w-4" />
            <span>{t('recentlySold.title')}</span>
          </div>
        </div>
        
        <div className="p-4">
          <div className="flex gap-3">
            {/* Vehicle Image */}
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
              {vehicle.images?.[0] ? (
                <img 
                  src={vehicle.images[0].url || vehicle.images[0]} 
                  alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Car className="h-8 w-8 text-slate-300" />
                </div>
              )}
            </div>
            
            {/* Vehicle Info */}
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-slate-900 truncate">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </h4>
              <p className="text-sm text-slate-500 flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {location}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {timeAgo}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Horizontal scrolling ticker for homepage
export function SoldTickerBanner() {
  const [soldVehicles, setSoldVehicles] = useState([]);

  useEffect(() => {
    const fetchSold = async () => {
      try {
        const response = await axios.get(`${API}/vehicles?limit=100&include_offline=true`);
        const sold = response.data
          .filter(v => v.status === 'sold')
          .slice(0, 15);
        setSoldVehicles(sold);
      } catch (error) {
        console.error('Error fetching sold vehicles:', error);
      }
    };

    fetchSold();
  }, []);

  if (soldVehicles.length < 3) return null;

  // Duplicate for seamless loop
  const tickerItems = [...soldVehicles, ...soldVehicles];

  return (
    <div className="bg-gradient-to-r from-green-600 to-emerald-600 py-2 overflow-hidden">
      <div className="flex animate-scroll-left">
        {tickerItems.map((vehicle, index) => (
          <div 
            key={`${vehicle.id}-${index}`}
            className="flex items-center gap-2 px-6 text-white text-sm whitespace-nowrap"
          >
            <CheckCircle className="h-4 w-4 text-green-200" />
            <span className="font-medium">{vehicle.year} {vehicle.make} {vehicle.model}</span>
            <span className="text-green-200">•</span>
            <span className="text-green-100">{getLocation(index)}</span>
            <span className="text-green-300 mx-4">|</span>
          </div>
        ))}
      </div>
      
      <style jsx="true">{`
        @keyframes scroll-left {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll-left {
          animation: scroll-left 30s linear infinite;
        }
        .animate-scroll-left:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
}

export default RecentlySoldTicker;
